import {
  SELECT_COLLECTION_ROUTINE,
  TOGGLE_SELECT_ALL_MEDIA,
  TOGGLE_SELECT_NONE_MEDIA,
  TOGGLE_COLLECTION_ASSET,
  FILTER_ASSETS_ROUTINE,
  UPDATE_COLLECTION_ASSET_ROUTINE,
  UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE,
  MERGE_COLLECTION_ASSET_VISION,
  CHANGE_ASSET_PAGINATION_ROUTINE,
  APPLY_BULK_TAGS_ROUTINE,
  INIT_MEDIA_LIBRARY_PAGE_ROUTINE,
  GET_ALL_COLLECTION_ASSETS_ROUTINE,
  DELETE_COLLECTION_ASSETS_ROUTINE,
} from "constants/ActionTypes";
import { createReducer } from "@reduxjs/toolkit";
import { normalizeEntity } from "reducers/reducerUtils";
import remove from "lodash/remove";

const initialState = {
  byId: {},
  allIds: [],
  selectedIds: [],
  allSelected: false,
  allCollectionAssets: [],
  total: 0,
  favoriteUpdates: {},
};

const collectionEntriesReducer = createReducer(initialState, (builder) => {
  builder.addCase(INIT_MEDIA_LIBRARY_PAGE_ROUTINE.TRIGGER, (state, action) => {
    return initialState;
  });
  builder.addCase(SELECT_COLLECTION_ROUTINE.SUCCESS, (state, action) =>
    setEntries(state, action)
  );
  builder.addCase(FILTER_ASSETS_ROUTINE.SUCCESS, (state, action) =>
    setEntries(state, action)
  );
  builder.addCase(CHANGE_ASSET_PAGINATION_ROUTINE.SUCCESS, (state, action) =>
    setEntries(state, action)
  );
  builder.addCase(TOGGLE_SELECT_ALL_MEDIA, (state, action) => {
    state.allSelected = !state.allSelected;
    state.selectedIds = state.selectedIds.filter(
      (assetId) => !state.allIds.includes(assetId)
    );
    if (state.allSelected) {
      state.selectedIds = state.selectedIds.concat(state.allIds);
    }
    return state;
  });
  builder.addCase(TOGGLE_SELECT_NONE_MEDIA, (state, action) => {
    state.allSelected = false;
    state.selectedIds = [];
    return state;
  });
  builder.addCase(TOGGLE_COLLECTION_ASSET, (state, action) => {
    const { asset } = action.payload;

    if (state.selectedIds.includes(asset.asset_id)) {
      remove(state.selectedIds, (selectedId) => selectedId === asset.asset_id);
    } else {
      state.selectedIds.push(asset.asset_id);
    }

    state.allSelected = state.allIds.every((id) =>
      state.selectedIds.find((selectedId) => selectedId === id)
    );

    return state;
  });
  builder.addCase(MERGE_COLLECTION_ASSET_VISION, (state, action) => {
    const { asset, vision, isReadResponse = false } = action.payload;
    const current = state.byId[asset.asset_id] || asset;
    if (
      current._id !== vision.inboxEntryId ||
      current.asset_id !== vision.assetId
    )
      return;
    const currentTags = current.tags ?? [];
    const requestedTags = asset.tags ?? [];
    const tagsUnchanged =
      currentTags.length === requestedTags.length &&
      currentTags.every((tag, index) => tag === requestedTags[index]);
    // Vision responses contain no caption, favorite or usage information.
    // A bulk/background save can keep the sheet mounted while a read is in
    // flight. For reads, merge only fields unchanged since the request started.
    // Manual analysis is a mutation returning its newly persisted tags.
    state.byId[asset.asset_id] = {
      ...current,
      visionDescription:
        !isReadResponse ||
        (current.visionDescription ?? "") === (asset.visionDescription ?? "")
          ? vision.visionDescription
          : current.visionDescription,
      tags: !isReadResponse || tagsUnchanged ? vision.tags : current.tags,
    };
  });
  builder.addCase(UPDATE_COLLECTION_ASSET_ROUTINE.SUCCESS, (state, action) => {
    const { asset } = action.payload;
    state.byId[asset.asset_id] = state.favoriteUpdates[asset.asset_id]
      ? { ...asset, favorite: state.byId[asset.asset_id]?.favorite }
      : asset;
    return state;
  });
  builder.addCase(
    UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.REQUEST,
    (state, action) => {
      const { assetId, favorite } = action.payload;
      if (state.byId[assetId]) state.byId[assetId].favorite = favorite;
      state.favoriteUpdates[assetId] = true;
    }
  );
  builder.addCase(
    UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.SUCCESS,
    (state, action) => {
      delete state.favoriteUpdates[action.payload.assetId];
    }
  );
  builder.addCase(
    UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.FAILURE,
    (state, action) => {
      const { assetId, favorite } = action.payload;
      if (state.byId[assetId]) state.byId[assetId].favorite = favorite;
      delete state.favoriteUpdates[assetId];
    }
  );
  builder.addCase(APPLY_BULK_TAGS_ROUTINE.SUCCESS, (state) => {
    state.allSelected = false;
    state.selectedIds = [];
    return state;
  });
  builder.addCase(
    GET_ALL_COLLECTION_ASSETS_ROUTINE.SUCCESS,
    (state, action) => {
      state.allCollectionAssets = action.payload.assets;
      return state;
    }
  );
  builder.addCase(DELETE_COLLECTION_ASSETS_ROUTINE.SUCCESS, (state, action) => {
    const deletedIdsSet = new Set(
      action.payload.assets.map((asset) => asset.asset_id)
    );
    state.selectedIds = state.selectedIds.filter(
      (id) => !deletedIdsSet.has(id)
    );
    return state;
  });
});

const setEntries = (state, action) => {
  const { entries } = action.payload;
  const { data, total } = entries;

  if (!data) {
    return state;
  }

  const { byId, allIds } = normalizeEntity(data, "asset_id");
  for (const assetId of allIds) {
    if (state.favoriteUpdates[assetId] && state.byId[assetId]) {
      byId[assetId] = {
        ...byId[assetId],
        favorite: state.byId[assetId].favorite,
      };
    }
  }
  const allSelected = allIds.every((id) =>
    state.selectedIds.find((selectedId) => selectedId === id)
  );

  return {
    ...state,
    byId: {
      ...state.byId,
      ...byId,
    },
    allIds,
    allSelected,
    total: total !== null && total !== undefined ? total : state.total,
  };
};

export default collectionEntriesReducer;
